import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit2, ArrowUp, ArrowDown, Sparkles, X, ChevronDown, ChevronUp } from "lucide-react";
import {
  researchContentSchema,
  type ResearchContent,
  type ResearchArea,
} from "@profolio/validators";

interface ResearchEditorProps {
  content: Record<string, unknown>;
  onChange: (content: ResearchContent) => void;
}

const emptyArea: ResearchArea = {
  title: "",
  description: "",
  keywords: [],
};

export function ResearchEditor({ content, onChange }: ResearchEditorProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<ResearchArea>(emptyArea);
  const [keywordInput, setKeywordInput] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [overviewOpen, setOverviewOpen] = useState(false);

  const parseResult = researchContentSchema.safeParse(content);
  const data: ResearchContent = parseResult.success
    ? parseResult.data
    : { heading: "Research Interests", summary: "", areas: [] };

  const heading = data.heading || "Research Interests";
  const summary = data.summary || "";
  const areas: ResearchArea[] = data.areas || [];

  const notifyChange = (
    newHeading: string,
    newSummary: string,
    newAreas: ResearchArea[]
  ) => {
    onChange({
      heading: newHeading,
      summary: newSummary,
      areas: newAreas,
    });
  };

  const openAddModal = () => {
    setEditingIndex(null);
    setFormData({ ...emptyArea });
    setKeywordInput("");
    setFormError(null);
    setModalOpen(true);
  };

  const openEditModal = (index: number) => {
    setEditingIndex(index);
    setFormData({ ...areas[index], keywords: [...(areas[index].keywords || [])] });
    setKeywordInput("");
    setFormError(null);
    setModalOpen(true);
  };

  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim();
    if (!trimmed) return;
    const current = formData.keywords || [];
    if (current.includes(trimmed)) return;
    if (current.length >= 20) return;

    setFormData({ ...formData, keywords: [...current, trimmed] });
    setKeywordInput("");
  };

  const handleRemoveKeyword = (kwIndex: number) => {
    const current = formData.keywords || [];
    setFormData({ ...formData, keywords: current.filter((_, i) => i !== kwIndex) });
  };

  const handleSaveArea = () => {
    if (!formData.title.trim()) {
      setFormError("Research area title is required.");
      return;
    }

    let updatedAreas: ResearchArea[];
    if (editingIndex !== null) {
      updatedAreas = [...areas];
      updatedAreas[editingIndex] = formData;
    } else {
      updatedAreas = [...areas, formData];
    }

    notifyChange(heading, summary, updatedAreas);
    setModalOpen(false);
  };

  const handleDeleteArea = (index: number) => {
    const updated = areas.filter((_, i) => i !== index);
    notifyChange(heading, summary, updated);
  };

  const moveArea = (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= areas.length) return;

    const updated = [...areas];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;

    notifyChange(heading, summary, updated);
  };

  return (
    <div className="space-y-6">
      {/* ── 1. Heading ── */}
      <div>
        <Label htmlFor="res-heading" className="text-xs font-semibold">
          Section Heading
        </Label>
        <Input
          id="res-heading"
          value={heading}
          onChange={(e) => notifyChange(e.target.value, summary, areas)}
          placeholder="Research Interests"
          className="mt-1 text-xs"
        />
      </div>

      {/* ── 2. Progressive Disclosure: Research Overview Narrative ── */}
      <div className="rounded-lg border bg-card/60 overflow-hidden">
        <button
          type="button"
          onClick={() => setOverviewOpen(!overviewOpen)}
          className="flex w-full items-center justify-between p-3 text-xs font-semibold hover:bg-muted/40 transition-colors"
        >
          <span>Research Overview Narrative & Statement</span>
          {overviewOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {overviewOpen && (
          <div className="p-4 pt-1 border-t space-y-2">
            <p className="text-[11px] text-muted-foreground">
              Optional overview paragraph introducing your lab's overarching agenda or major scientific questions.
            </p>
            <Textarea
              id="res-summary"
              value={summary}
              onChange={(e) => notifyChange(heading, e.target.value, areas)}
              placeholder="My research investigates high-throughput distributed systems..."
              rows={4}
              className="text-xs"
            />
          </div>
        )}
      </div>

      {/* ── 3. Research Areas List ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-xs font-semibold">Research Areas & Pillars ({areas.length})</Label>
            <p className="text-[11px] text-muted-foreground">
              Define your core specializations and primary methodology domains.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={openAddModal}
            className="gap-1.5 text-xs h-8"
          >
            <Plus className="h-3.5 w-3.5" /> Add Research Area
          </Button>
        </div>

        {areas.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
            <Sparkles className="h-8 w-8 text-muted-foreground/60 mb-2" />
            <p className="text-sm font-medium">No research areas added yet</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm">
              Break down your primary research themes, methodologies, and keyword tags.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openAddModal}
              className="mt-4 gap-1.5 text-xs"
            >
              <Plus className="h-3.5 w-3.5" /> Add First Area
            </Button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {areas.map((area, index) => (
              <div
                key={area.id || index}
                className="flex items-start justify-between gap-3 rounded-lg border bg-card p-3.5 shadow-2xs hover:border-primary/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-xs text-foreground">{area.title}</h4>
                  {area.description && (
                    <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                      {area.description}
                    </p>
                  )}
                  {area.keywords && area.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {area.keywords.map((kw, kwIdx) => (
                        <Badge key={kwIdx} variant="secondary" className="text-[10px] px-1.5 py-0">
                          {kw}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={index === 0}
                    onClick={() => moveArea(index, "up")}
                    className="h-7 w-7 text-muted-foreground"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={index === areas.length - 1}
                    onClick={() => moveArea(index, "down")}
                    className="h-7 w-7 text-muted-foreground"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditModal(index)}
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteArea(index)}
                    className="h-7 w-7 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Add / Edit Research Area Dialog ── */}
      <Dialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editingIndex !== null ? "Edit Research Area" : "Add Research Area"}
        description="Describe this domain of focus and add related keywords."
      >
        <div className="space-y-4 pt-2">
          {formError && (
            <div className="rounded-md bg-destructive/15 p-2.5 text-xs text-destructive font-medium">
              {formError}
            </div>
          )}

          <div>
            <Label htmlFor="area-title" className="text-xs font-semibold">
              Research Area Title *
            </Label>
            <Input
              id="area-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g. Distributed Consensus & Cloud Storage"
              className="mt-1 text-xs"
            />
          </div>

          <div>
            <Label htmlFor="area-desc" className="text-xs font-semibold">
              Description & Objectives
            </Label>
            <Textarea
              id="area-desc"
              value={formData.description || ""}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Explain the scope, hypotheses, and goals in this domain..."
              rows={3}
              className="mt-1 text-xs"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold">Keywords & Methodology Tags</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddKeyword();
                  }
                }}
                placeholder="Type tag and press Enter (e.g. Paxos, Raft, Edge)"
                className="text-xs flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddKeyword}
                className="text-xs shrink-0 h-8"
              >
                Add Tag
              </Button>
            </div>

            {formData.keywords && formData.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2.5 p-2 rounded-md bg-muted/40 border">
                {formData.keywords.map((kw, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 rounded bg-background px-2 py-0.5 text-xs border font-medium"
                  >
                    {kw}
                    <button
                      type="button"
                      onClick={() => handleRemoveKeyword(i)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t pt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
              className="text-xs h-8"
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleSaveArea} className="text-xs h-8 font-medium">
              {editingIndex !== null ? "Save Changes" : "Add Area"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
